import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, Stack } from '@mui/material';
import { useTranslation } from '@/hooks/useTranslation';
import { RU, EN, InfoSection } from '@/content/infoContent';

const SECTION_ID_PREFIX = 'info-section-';

const InfoPage: React.FC = () => {
  const { getCurrentLanguage } = useTranslation();
  const lang = getCurrentLanguage?.() || '';
  const langLower = String(lang).toLowerCase();
  // German and English both show English content; other languages (e.g. Russian) show RU
  const useEnglish = langLower.startsWith('de') || langLower.startsWith('en');
  const sections: InfoSection[] = useEnglish ? EN : RU;

  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const updateActive = () => {
      const threshold = 120;
      let next = 0;
      for (let i = 0; i < sections.length; i++) {
        const el = sectionRefs.current[i];
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (top <= threshold) next = i;
        }
      }
      setActiveIndex((prev) => (prev !== next ? next : prev));
    };
    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    return () => window.removeEventListener('scroll', updateActive);
  }, [sections.length]);

  const handleNavClick = (i: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(`${SECTION_ID_PREFIX}${i}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderBody = (body: string) =>
    body
      .split(/\n\n+/)
      .filter(Boolean)
      .map((p, i) => (
        <Typography
          key={i}
          component="p"
          variant="body1"
          sx={{ mb: 1.5, whiteSpace: 'pre-line' }}
        >
          {p}
        </Typography>
      ));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', p: 3, gap: 3, alignItems: 'flex-start' }}>
      {/* Sticky nav */}
      <Stack
        component="nav"
        sx={{
          position: 'sticky',
          top: 88,
          width: 220,
          flexShrink: 0,
          py: 1,
        }}
      >
        {sections.map((s, i) => (
          <Typography
            key={i}
            component="a"
            href={`#${SECTION_ID_PREFIX}${i}`}
            onClick={handleNavClick(i)}
            sx={{
              display: 'block',
              py: 0.75,
              px: 1.5,
              fontSize: '0.875rem',
              color: activeIndex === i ? 'primary.main' : 'text.secondary',
              fontWeight: activeIndex === i ? 600 : 400,
              borderLeft: 3,
              borderColor: 'transparent',
              borderLeftColor: activeIndex === i ? 'primary.main' : 'transparent',
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            {s.title}
          </Typography>
        ))}
      </Stack>

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {useEnglish ? 'Legal Information' : 'Правовая информация'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {useEnglish
            ? 'Terms of Use for Chip Tuning LLC and Usage Agreement & Liability Disclaimer'
            : 'Условия использования Chip Tuning LLC и соглашение об отказе от ответственности'}
        </Typography>
        {sections.map((s, i) => (
          <Paper
            key={i}
            id={`${SECTION_ID_PREFIX}${i}`}
            ref={(el) => {
              sectionRefs.current[i] = el;
            }}
            variant="outlined"
            sx={{ p: 3, mb: 3 }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              component="h2"
            >
              {s.title}
            </Typography>
            <Box>{renderBody(s.body)}</Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default InfoPage;
